import {
  CdkDragDrop,
  CdkDragEnter,
  CdkDragStart,
  CdkDropList,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { asapScheduler } from 'rxjs';

import {
  ComparisonOperator,
  LogicalType,
  QueryNode,
  QueryNodeType,
} from '@blaze-shared/api-contracts-domain';
import { CheckboxComponent, ClickableDirective, TooltipDirective } from '@blaze-shared/shared-ui';

import {
  ComparisonQueryFormGroup,
  LogicalQueryFormGroup,
  QueryRowDragDrop,
} from '../../complex-filter.models';
import {
  COMPLEX_FILTER_SERVICE,
  ComplexFilterService,
} from '../../providers/complex-filter.providers';
import { DragDropListService } from '../../providers/drag-drop-list.service';
import { ComparisonQueryRowComponent } from '../comparison-query-row/comparison-query-row.component';

@UntilDestroy()
@Component({
  selector: '[formGroup] blaze-logical-query-row, [formGroupName] blaze-logical-query-row',
  imports: [
    CheckboxComponent,
    ClickableDirective,
    ComparisonQueryRowComponent,
    DragDropModule,
    FormsModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TooltipDirective,
  ],
  templateUrl: './logical-query-row.component.html',
  styleUrls: ['./logical-query-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogicalQueryRowComponent implements OnInit, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly controlContainer = inject(ControlContainer);
  private readonly dragDropListService = inject(DragDropListService);
  complexFilterService = inject<ComplexFilterService>(COMPLEX_FILTER_SERVICE);

  delete = output<AbstractControl>();
  itemDrop = output<QueryRowDragDrop>();

  @Input() @HostBinding('class.with-theme') useAppTheme = false;
  @Input() disabled = false;
  @Input() isInOrganizeMode = false;
  @Input() layout: 'row' | 'column' = 'row';
  @Input() level = 0;

  @ViewChild(CdkDropList) cdkDropList: CdkDropList | undefined;

  protected hideIgnoreWhenEmptyToggles = signal<{ [key: string]: boolean }>({});

  readonly showIgnoreWhenEmptyToggle = signal(false);
  connectedDropLists: CdkDropList[] = [];

  formGroup: FormGroup<LogicalQueryFormGroup> | null = null;
  get logicalType(): string {
    if (!this.formGroup) {
      return '';
    }

    return this.formGroup.value.logicalType === LogicalType.And ? 'and' : 'or';
  }

  get isRootLevel(): boolean {
    return this.level === 0;
  }

  constructor() {
    this.showIgnoreWhenEmptyToggle.set(this.complexFilterService.showIgnoreWhenEmptyToggle());
  }

  ngOnInit(): void {
    this.formGroup = this.controlContainer.control as FormGroup<LogicalQueryFormGroup>;

    if (this.isRootLevel) {
      this.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }

    this.dragDropListService
      .getLists$()
      .pipe(untilDestroyed(this))
      .subscribe((lists) => {
        this.connectedDropLists = lists;
      });
  }

  ngAfterViewInit(): void {
    if (this.cdkDropList) {
      this.dragDropListService.addConnectedDropList(this.cdkDropList);
    }
  }

  toggleLogicalType(formGroup: FormGroup<LogicalQueryFormGroup>): void {
    formGroup.patchValue({
      logicalType:
        formGroup.value.logicalType === LogicalType.And ? LogicalType.Or : LogicalType.And,
    });

    this.cdr.markForCheck();
  }

  onDrop(event: CdkDragDrop<QueryNode[]>): void {
    if (!this.formGroup) {
      return;
    }
    this.itemDrop.emit({
      event,
      targetFormGroup: this.formGroup,
    });
  }

  onChildDrop({ event, targetFormGroup }: QueryRowDragDrop): void {
    this.itemDrop.emit({
      event,
      targetFormGroup: targetFormGroup,
    });
  }

  isLogicalQueryRow(control: AbstractControl): control is FormGroup<LogicalQueryFormGroup> {
    return control.value.nodeType === QueryNodeType.Logical;
  }

  isComparisonQueryRow(control: AbstractControl): control is FormGroup<ComparisonQueryFormGroup> {
    return control.value.nodeType === QueryNodeType.Comparison;
  }

  onDragEntered(event: CdkDragEnter): void {
    this.enforceDragToSelf(event.container);
  }

  onDragStarted(event: CdkDragStart): void {
    this.enforceDragToSelf(event.source.dropContainer);
  }

  protected onComparisonRowOperatorUpdate(
    op: ComparisonOperator,
    control: FormGroup<ComparisonQueryFormGroup>,
  ): void {
    const formCtrlId = control?.value?.formControlId;
    if (!formCtrlId) {
      return;
    }

    const showToggles = { ...this.hideIgnoreWhenEmptyToggles() };
    if (op === ComparisonOperator.IsNull || op === ComparisonOperator.IsNotNull) {
      showToggles[formCtrlId] = true;
    } else {
      showToggles[formCtrlId] = !this.complexFilterService.showIgnoreWhenEmptyToggle();
    }

    this.hideIgnoreWhenEmptyToggles.set(showToggles);
  }

  private enforceDragToSelf(dl: CdkDropList): void {
    const siblings = dl.connectedTo as CdkDropList<any>[];

    const ref = dl._dropListRef;
    asapScheduler.schedule(() => {
      ref.connectedTo(siblings.map((list) => list._dropListRef));
    });
  }
}
